import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useCreateNoteMutation, useUploadNoteImagesMutation } from '../../slices/notesApiSlice';
import { evaluate } from 'mathjs';

// Utility functions
const isValidText = (text) => /^[\w\s.,'-]{2,}$/.test(text.trim());

const validateFiles = (files) => {
  const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
  const maxSize = 5 * 1024 * 1024;
  for (let file of files) {
    if (!validTypes.includes(file.type)) {
      toast.error(`Invalid file type: ${file.type}`);
      return false;
    }
    if (file.size > maxSize) {
      toast.error(`File size exceeds limit: ${file.name}`);
      return false;
    }
  }
  return true;
};

const AddUserNote = () => {
  const navigate = useNavigate();

  // Default values
  const today = new Date().toISOString().split('T')[0];

  // Form state
  const [date, setDate] = useState(today);
  const [status, setStatus] = useState('Pending');
  const [accountName, setAccountName] = useState('');
  const [details, setDetails] = useState('');
  const [number, setNumber] = useState(0);
  const [images, setImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [calcDisplay, setCalcDisplay] = useState('');

  const [createNote, { isLoading }] = useCreateNoteMutation();
  const [uploadNoteImages] = useUploadNoteImagesMutation();

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'date') setDate(value);
    else if (name === 'status') setStatus(value);
    else if (name === 'accountName') setAccountName(value);
    else if (name === 'details') setDetails(value);
    else if (name === 'number') setNumber(value);
  };

  const uploadImageHandler = async (files) => {
    if (!validateFiles(files)) return;

    const previews = Array.from(files).map((file) => URL.createObjectURL(file));
    setPreviewImages((prev) => [...prev, ...previews]);

    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('images', file));
    try {
      const res = await uploadNoteImages(formData).unwrap();
      toast.success(res.message);
      setImages((prev) => [...prev, ...res.images]);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const removeImageHandler = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleCalculatorClick = (value) => {
    if (value === '=') {
      try {
        setCalcDisplay(evaluate(calcDisplay).toString());
      } catch {
        setCalcDisplay('Error');
      }
    } else if (value === 'C') {
      setCalcDisplay('');
    } else {
      setCalcDisplay((prev) => prev + value);
    }
  };

  const isFormValid = () =>
    isValidText(accountName) &&
    isValidText(details) &&
    date !== '' &&
    status !== '';

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      await createNote({
        date,
        status,
        accountName,
        details,
        number,
        images,
      }).unwrap();
      toast.success('Note created successfully');
      navigate('/');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  return (
    <div className="container mt-4">
      <h1>Create New Note</h1>
      <Form onSubmit={submitHandler}>
        <Form.Group controlId="date" className="mb-3" style={{display: "none"}}>
          <Form.Label>Date</Form.Label>
          <Form.Control type="date" name="date" value={date} onChange={handleChange} required />
        </Form.Group>

        <Form.Group controlId="status" className="mb-3" style={{display: "none"}}>
          <Form.Label>Status</Form.Label>
          <Form.Control as="select" name="status" value={status} onChange={handleChange} required>
            <option value="Completed">Completed</option>
            <option value="Pending">Pending</option>
          </Form.Control>
        </Form.Group>

        <Form.Group controlId="accountName" className="mb-3">
          <Form.Label>Account Name</Form.Label>
          <Form.Control
            type="text"
            name="accountName"
            value={accountName}
            onChange={handleChange}
            placeholder="Enter your name"
            required
          />
        </Form.Group>

        <Form.Group controlId="details" className="mb-3">
          <Form.Label>Message</Form.Label>
          <Form.Control
            type="text"
            name="details"
            value={details}
            onChange={handleChange}
            placeholder="Write your message"
            required
          />
        </Form.Group>

        <Form.Group controlId="number" className="mb-3" style={{display: "none"}}>
          <Form.Label>Number</Form.Label>
          <Form.Control
            type="number"
            name="number"
            value={number}
            onChange={handleChange}
            placeholder="Enter number"
            required
          />
        </Form.Group>

        {/* Mini Calculator */}
        <div className="mb-4 p-3 border rounded" style={{ maxWidth: '230px', display: "none" }}>
          <Form.Label>Mini Calculator</Form.Label>
          <Form.Control type="text" value={calcDisplay} readOnly className="mb-2" />
          {['7', '8', '9', '/', '4', '5', '6', '*', '1', '2', '3', '-', '0', '.', '=', '+', 'C'].map((char) => (
            <Button
              key={char}
              variant="light"
              className="m-1"
              onClick={() => handleCalculatorClick(char)}
              style={{ width: '40px' }}
            >
              {char}
            </Button>
          ))}
          <div className="mt-2">
            <Button
              variant="outline-primary"
              onClick={() => setNumber(calcDisplay)}
              disabled={calcDisplay === '' || calcDisplay === 'Error'}
            >
              Copy to Amount Field
            </Button>
          </div>
        </div>

        <Form.Group controlId="images" className="mb-3">
          <Form.Label>Images</Form.Label>
          <Form.Control type="file" multiple onChange={(e) => uploadImageHandler(e.target.files)} />
          <div className="mt-2 d-flex flex-wrap gap-3">
            {previewImages.map((url, idx) => (
              <img key={`preview-${idx}`} src={url} alt="preview" style={{ maxWidth: '100px' }} />
            ))}
            {images.map((img, idx) => (
              <div key={idx} style={{ position: 'relative' }}>
                <img src={img} alt={`Note ${idx}`} style={{ maxWidth: '100px' }} />
                <button
                  onClick={() => removeImageHandler(idx)}
                  style={{ position: 'absolute', top: '0', right: '0' }}
                >
                  ✖
                </button>
              </div>
            ))}
          </div>
        </Form.Group>

        <Button type="submit" variant="primary" disabled={!isFormValid() || isLoading}>
          {isLoading ? 'Creating...' : 'Send Message'}
        </Button>
      </Form>
    </div>
  );
};

export default AddUserNote;